import { useCallback, useEffect, useRef, useState } from "react";

import {
  addEquipmentZoneLink,
  createEquipment,
  deleteEquipment,
  getBuildingLocations,
  getDataPointHistory,
  listBuildingFindings,
  listDataPointSources,
  listEquipment,
  listZones,
  removeEquipmentZoneLink,
  updateEquipment,
  type DataPointSample,
  type DataPointSource,
  type Equipment,
  type FindingSeverity,
  type Zone as ApiZone,
} from "@/lib/api/building";
import {
  optimizationColor,
  resolvedEquipmentZoneIds,
  type DataPoint,
  type EquipmentCard,
  type Zone,
} from "@/lib/building-domain";
import {
  equipmentTypeIcon,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { labelsFromSources, unitsFromOneSource } from "@/lib/data-point-labels";
import { formatSignalValue } from "@/lib/format-signal-value";

import { EquipmentGrid } from "./EquipmentGrid";

interface Props {
  buildingId: string;
  operator?: boolean;
  // Equipment↔zone links are a structural reconfiguration — configurator-only (auth.ts),
  // stricter than the operator gate on the rest of the edit form.
  configurator?: boolean;
  // Bump to force a re-fetch (e.g. after a Sijainti/zone is added elsewhere); onChanged fires
  // after this view's own create/update/delete so callers can refresh in step.
  refreshKey?: number;
  onChanged?: () => void;
}

function sample_to_data_point(
  s: DataPointSample,
  unit: string | undefined,
): DataPoint {
  return {
    domain: s.domain,
    nature: s.nature,
    name: s.name,
    value: formatSignalValue(s, unit),
    numeric_value: s.numeric_value ?? undefined,
    unit: unit ?? s.unit ?? undefined,
    source: "sensor",
    recorded_at: s.recorded_at,
  };
}

function build_live_data_points_map(
  sources: DataPointSource[],
  samples: DataPointSample[],
): Map<string, DataPoint[]> {
  const result = new Map<string, DataPoint[]>();
  for (const src of sources) {
    const units = unitsFromOneSource(src);
    const src_samples = samples.filter((s) => s.source_id === src.source_id);
    const latest = new Map<string, DataPointSample>();
    for (const s of src_samples) {
      const prev = latest.get(s.name);
      if (!prev || s.recorded_at > prev.recorded_at) latest.set(s.name, s);
    }
    result.set(
      src.source_id,
      [...latest.values()].map((s) => sample_to_data_point(s, units[s.name])),
    );
  }
  return result;
}

function to_zone(z: ApiZone): Zone {
  return {
    id: z.id,
    building_id: z.building_id,
    name: z.name,
    floor: z.floor,
    icon: z.icon,
    zone_type: z.zone_type,
    description: z.description,
    updated_at: z.updated_at,
  };
}

function to_equipment_card(e: Equipment): EquipmentCard {
  const pct =
    typeof e.state["optimization_pct"] === "number"
      ? (e.state["optimization_pct"] as number)
      : 0;
  const raw_dp = e.state["data_points"];
  const raw_kpis = e.state["kpis"];
  return {
    id: e.id,
    zone_id: e.zone_id,
    zone_ids: resolvedEquipmentZoneIds(e),
    location: e.location,
    name: e.name,
    equipment_type: e.equipment_type,
    equipment_tag: e.equipment_tag,
    // Vestigial on the card — panels read `equipmentTypeIcon(eqMeta, type)`.
    icon: "⚙️",
    color: "#5b8dee",
    optimization_pct: pct,
    optimization_color: optimizationColor(pct),
    optimization_enabled: e.optimization_enabled,
    kpis: Array.isArray(raw_kpis)
      ? (raw_kpis as { label: string; value: string }[])
      : [],
    data_points: Array.isArray(raw_dp) ? (raw_dp as DataPoint[]) : [],
    live_data_points: [],
    state: e.state,
    declared_variants: e.declared_variants ?? [],
    declared_data_points: e.declared_data_points ?? [],
    declared_component_order: e.declared_component_order ?? [],
    declared_component_subtypes: e.declared_component_subtypes ?? {},
    declared_stream_component_order: e.declared_stream_component_order ?? {},
  };
}

export function EquipmentView({
  buildingId,
  operator = false,
  configurator = false,
  refreshKey = 0,
  onChanged,
}: Props) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const [equipment, set_equipment] = useState<EquipmentCard[]>([]);
  const [zones, set_zones] = useState<Zone[]>([]);
  const [locations, set_locations] = useState<string[]>([]);
  const [behavior_labels, set_behavior_labels] = useState<
    Record<string, string>
  >({});
  const [severity_map, set_severity_map] = useState<
    Map<string, FindingSeverity>
  >(new Map());
  const [loading, set_loading] = useState(true);
  // Guards against overlapping load() calls (StrictMode's dev double-invoke, or a fast
  // refreshKey bump racing the initial mount) committing a superseded response — the older
  // call would otherwise still set_zones/set_equipment with a stale-but-different array
  // reference. Mirrors ZonesView.tsx's load_token guard.
  const load_token = useRef(0);

  const load = useCallback(async () => {
    const token = ++load_token.current;
    const [raw_zones, raw_equipment, raw_locations] = await Promise.all([
      listZones(buildingId),
      listEquipment(buildingId),
      getBuildingLocations(buildingId).catch((): string[] => []),
    ]);
    if (load_token.current !== token) return; // a newer load() has since started
    set_locations(raw_locations);
    set_zones(raw_zones.map(to_zone));

    const fetch_live = async () => {
      const all_sources = await listDataPointSources(buildingId).catch(
        (): DataPointSource[] => [],
      );
      set_behavior_labels(labelsFromSources(all_sources));
      const eq_sources = all_sources.filter(
        (s) => s.source_type === "equipment",
      );
      const eq_source_ids = eq_sources.map((s) => s.source_id);
      const eq_names = [
        ...new Set(
          eq_sources.flatMap((s) => s.available_names.map((n) => n.name)),
        ),
      ];
      let live_samples: DataPointSample[] = [];
      if (eq_source_ids.length > 0 && eq_names.length > 0) {
        const from_ts = new Date(Date.now() - 48 * 3_600_000).toISOString();
        live_samples = await getDataPointHistory(
          buildingId,
          eq_source_ids,
          eq_names,
          from_ts,
        ).catch((): DataPointSample[] => []);
      }
      return build_live_data_points_map(eq_sources, live_samples);
    };

    const live_by_equipment = await fetch_live();
    if (load_token.current !== token) return; // a newer load() has since started

    const cards = raw_equipment.map((e) => ({
      ...to_equipment_card(e),
      icon: equipmentTypeIcon(eqMeta, e.equipment_type, sensors),
      live_data_points: live_by_equipment.get(e.id) ?? [],
    }));
    set_equipment(cards);

    set_loading(false);
  }, [buildingId, eqMeta, sensors]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    listBuildingFindings(buildingId, { status: "open" })
      .then((findings) => {
        const m = new Map<string, FindingSeverity>();
        for (const f of findings) {
          const prev = m.get(f.source_id);
          if (
            !prev ||
            f.severity === "critical" ||
            (f.severity === "warning" && prev === "info")
          ) {
            m.set(f.source_id, f.severity);
          }
        }
        set_severity_map(m);
      })
      .catch(() => {});
  }, [buildingId]);

  async function handle_create(data: {
    name: string;
    equipment_type: string;
    equipment_tag: string;
    zone_ids: string[];
    location: string | null;
  }) {
    await createEquipment(buildingId, {
      name: data.name,
      equipment_type: data.equipment_type,
      equipment_tag: data.equipment_tag,
      zone_id: data.zone_ids[0] ?? null,
      location: data.location,
    });
    await load();
    onChanged?.();
  }

  async function handle_update(
    id: string,
    data: {
      name?: string;
      equipment_type?: string;
      equipment_tag?: string;
      location?: string | null;
      state?: Record<string, unknown>;
      optimization_enabled?: boolean;
      declared_variants?: string[];
      declared_component_order?: string[];
      declared_component_subtypes?: Record<string, string>;
      declared_stream_component_order?: Record<string, string[]>;
    },
  ) {
    // Zones are managed via the dedicated zone-links endpoints, not this update.
    await updateEquipment(buildingId, id, data);
    await load();
    onChanged?.();
  }

  async function handle_delete(id: string) {
    await deleteEquipment(buildingId, id);
    await load();
    onChanged?.();
  }

  // Zone links go through the dedicated zone-links endpoints — never the updateEquipment
  // zone_id bypass, which can only hold a single zone.
  async function handle_add_zone_link(equipmentId: string, zoneId: string) {
    await addEquipmentZoneLink(buildingId, equipmentId, zoneId);
    await load();
    onChanged?.();
  }

  async function handle_remove_zone_link(equipmentId: string, zoneId: string) {
    await removeEquipmentZoneLink(buildingId, equipmentId, zoneId);
    await load();
    onChanged?.();
  }

  if (loading) {
    return (
      <p className="mt-8 text-center text-sm text-muted-foreground">
        Ladataan...
      </p>
    );
  }

  return (
    <EquipmentGrid
      buildingId={buildingId}
      equipment={equipment}
      zones={zones}
      locations={locations}
      operator={operator}
      configurator={configurator}
      on_create={handle_create}
      on_update={handle_update}
      on_delete={handle_delete}
      on_add_zone_link={handle_add_zone_link}
      on_remove_zone_link={handle_remove_zone_link}
      behavior_labels={behavior_labels}
      findings_severity={severity_map}
    />
  );
}
